import {
  FocusValidity,
  GlobalBrand,
  GlobalBrandRequest,
  Query,
  QueryChange,
  QueryUses,
  SearchQueryInfo,
  SearchableQuery,
} from "@radarly/models/queries";
import { QueryDetails } from "@radarly/models/queries";
import { RadarlyAPI } from "./base";

interface QuerySearchOptions {
  includeLabel: boolean;
  includeQueryBody: boolean;
}
interface Pagination {
  start: number;
  limit: number;
}

export class QueryAPI extends RadarlyAPI {
  public async getQueryValidity(query: string) {
    return await this.post<SearchQueryInfo>("/query/info.json", { query });
  }
  public async getQueryDetails(project: number, focus: number) {
    return await this.get<QueryDetails>(
      `/projects/${project}/focuses/${focus}/details.json`
    );
  }
  public async searchQueries(
    search: string,
    pagination: Partial<Pagination> = {},
    options: Partial<QuerySearchOptions> = {}
  ) {
    const page = Math.round((pagination.start || 0) / (pagination.limit || 15));
    return await this.post<{ queries: SearchableQuery[]; total: number }>(
      "/queries/search.json",
      {
        query: search,
        nb: pagination.limit || 15,
        page,
        ...options,
      }
    );
  }
  public async getQueryUses(project: number, focus: number) {
    return await this.get<QueryUses>(
      `/projects/${project}/focuses/${focus}/uses.json`
    );
  }
  public async getQueriesUses(project: number, focuses: number[]) {
    return await Promise.all(
      focuses.map((focus) => this.getQueryUses(project, focus))
    );
  }
  public async getQueryProjectPictures(project: number) {
    return await this.get<{ pictures: { id: number; url: string }[] }>(
      `/projects/${project}/pictures.json`
    );
  }
  public async deleteQuery(project: number, focus: number) {
    return await this.delete<void>(
      `/projects/${project}/focuses/${focus}.json`
    );
  }
  public async deleteQueries(project: number, focuses: number[]) {
    const res = await Promise.allSettled(
      focuses.map(async (focus) => {
        try {
          await this.deleteQuery(project, focus);
          return focus;
        } catch (err) {
          return Promise.reject(focus);
        }
      })
    );
    return res;
  }

  public async deleteQueryProjectPicture(project: number, pictureId: number) {
    return await this.delete<void>(
      `/projects/${project}/pictures/${pictureId}.json`
    );
  }
  public async createQuery(project: number, query: Query) {
    return await this.post<Query>(
      `/projects/${project}/focuses.json`,
      query,
      true
    );
  }
  public async updateQuery(project: number, query: Query) {
    return await this.put<Query>(
      `/projects/${project}/focuses/${query.id}.json`,
      query
    );
  }

  public async uploadQueryProjectPicture(projectId: number, picture: File) {
    const formData = new FormData();
    formData.append("image", picture);

    return await this.upload<{ id: number; url: string }>(
      `/projects/${projectId}/pictures.json`,
      formData
    );
  }

  public async uploadQueryProjectPictures(projectId: number, files: File[]) {
    return await Promise.all(
      files.map((picture) => this.uploadQueryProjectPicture(projectId, picture))
    );
  }
  public async checkQueryLimit(project: number, query: Query) {
    return await this.post<{ isOverLimit: boolean }>(
      `/projects/${project}/focus-check-query-limits.json`,
      query
    );
  }
  public async getGlobalBrands(query: GlobalBrandRequest, projectId: number) {
    return await this.post<{ data: GlobalBrand[]; total: number }>(
      `/projects/${projectId}/global-queries.json`,
      query
    );
  }
  public async getFocusValidity(query: Query, projectId: number) {
    return await this.post<{ data: FocusValidity }>(
      `/projects/${projectId}/focus-check/${query.id}.json`,
      query,
      true
    );
  }
  public async getQueryChangeLog(
    project: number,
    focus: number,
    pagination: Pagination
  ) {
    return await this.post<{ changelog: QueryChange[]; total: number }>(
      `/projects/${project}/focuses/changelog`,
      {
        focusId: focus,
        pagination,
      }
    );
  }
}
